import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import { Spinner } from "@woba/ds-web";
import { CONTRACT_MANAGEMENT_ROUTES } from "./constants/contract-management-routes";

const CommitmentListPage = /* @__PURE__ */ lazy(
  () => import("./pages/commitments/commitment-list-page"),
);
const CommitmentCreatePage = /* @__PURE__ */ lazy(
  () => import("./pages/commitments/commitment-create-page"),
);
const ClientListPage = /* @__PURE__ */ lazy(
  () => import("./pages/clients/client-list-page"),
);
const CompanyDetailPage = /* @__PURE__ */ lazy(
  () => import("./pages/clients/company-detail-page"),
);
const GroupDetailPage = /* @__PURE__ */ lazy(
  () => import("./pages/clients/group-detail-page"),
);
const CompanyCreatePage = /* @__PURE__ */ lazy(
  () => import("./pages/clients/company-create-page"),
);
const GroupCreatePage = /* @__PURE__ */ lazy(
  () => import("./pages/clients/group-create-page"),
);
const PartnerListPage = /* @__PURE__ */ lazy(
  () => import("./pages/partners/partner-list-page"),
);
const PartnerDetailPage = /* @__PURE__ */ lazy(
  () => import("./pages/partners/partner-detail-page"),
);
const CommitmentDetailPage = /* @__PURE__ */ lazy(
  () => import("./pages/commitments/commitment-detail-page"),
);

const AllocationsListPage = /* @__PURE__ */ lazy(() =>
  import("./pages/items/items-list-page").then((module) => ({
    default: module.AllocationsListPage,
  })),
);
const ContributionsListPage = /* @__PURE__ */ lazy(() =>
  import("./pages/items/items-list-page").then((module) => ({
    default: module.ContributionsListPage,
  })),
);

const AllocationDetailPage = /* @__PURE__ */ lazy(
  () => import("./pages/items/allocation-detail-page"),
);

const ContributionDetailPage = /* @__PURE__ */ lazy(
  () => import("./pages/items/contribution-detail-page"),
);

const ExtraCreditsPage = /* @__PURE__ */ lazy(
  () => import("./pages/items/extra-credits-page"),
);

const page = (element: ReactNode) => (
  <Suspense fallback={<Spinner size="lg" />}>{element}</Suspense>
);

const relative = (path: string) =>
  path.replace(`${CONTRACT_MANAGEMENT_ROUTES.ROOT}/`, "");

/**
 * Child routes of /gestao-contratual, rendered inside ContractManagementLayout.
 * Every page is lazy and marked pure, so a build with the module switched off
 * (production) drops all of them: see `src/router.tsx`.
 */
// Built inside a pure call: `relative` reads a string the bundler cannot
// prove safe, and without the mark it would keep every page in production.
export const contractManagementChildRoutes: RouteObject[] =
  /* @__PURE__ */ (() => [
    {
      index: true,
      element: <Navigate to={CONTRACT_MANAGEMENT_ROUTES.COMMITMENTS} replace />,
    },
    {
      path: relative(CONTRACT_MANAGEMENT_ROUTES.COMMITMENTS),
      element: page(<CommitmentListPage />),
    },
    {
      path: relative(CONTRACT_MANAGEMENT_ROUTES.COMMITMENT_CREATE),
      element: page(<CommitmentCreatePage />),
    },
    {
      path: relative(CONTRACT_MANAGEMENT_ROUTES.COMMITMENT_DETAIL),
      element: page(<CommitmentDetailPage />),
    },
    {
      path: relative(CONTRACT_MANAGEMENT_ROUTES.ALLOCATIONS),
      element: page(<AllocationsListPage />),
    },
    {
      path: relative(CONTRACT_MANAGEMENT_ROUTES.ALLOCATION_DETAIL),
      element: page(<AllocationDetailPage />),
    },
    {
      path: relative(CONTRACT_MANAGEMENT_ROUTES.CONTRIBUTIONS),
      element: page(<ContributionsListPage />),
    },
    {
      path: relative(CONTRACT_MANAGEMENT_ROUTES.EXTRA_CREDITS),
      element: page(<ExtraCreditsPage />),
    },
    {
      path: relative(CONTRACT_MANAGEMENT_ROUTES.CONTRIBUTION_DETAIL),
      element: page(<ContributionDetailPage />),
    },
    {
      path: relative(CONTRACT_MANAGEMENT_ROUTES.CLIENTS),
      element: page(<ClientListPage />),
    },
    {
      path: relative(CONTRACT_MANAGEMENT_ROUTES.COMPANY_CREATE),
      element: page(<CompanyCreatePage />),
    },
    {
      path: relative(CONTRACT_MANAGEMENT_ROUTES.GROUP_CREATE),
      element: page(<GroupCreatePage />),
    },
    {
      path: relative(CONTRACT_MANAGEMENT_ROUTES.COMPANY_DETAIL),
      element: page(<CompanyDetailPage />),
    },
    {
      path: relative(CONTRACT_MANAGEMENT_ROUTES.GROUP_DETAIL),
      element: page(<GroupDetailPage />),
    },
    {
      path: relative(CONTRACT_MANAGEMENT_ROUTES.PARTNERS),
      element: page(<PartnerListPage />),
    },
    {
      path: relative(CONTRACT_MANAGEMENT_ROUTES.PARTNER_DETAIL),
      element: page(<PartnerDetailPage />),
    },
  ])();
